// Shared edge-aware clamp for the fast CSS tooltip (the `data-tooltip`
// pattern from QA #9). Design-review fix 3: a centered tooltip on a trigger
// near the chat-frame edge extended past the frame and got cut — worse on
// long texts.
//
// A pure-CSS ::after can't measure its own position, so a tiny JS assist
// runs on pointerover/focusin (delegated on the row container): it compares
// the trigger's rect against the chat frame's rect and
//   - sets `--emma-tt-max` on the trigger so the tooltip can never be wider
//     than the frame (long texts WRAP via the CSS `white-space: normal`),
//   - sets `data-tt-align="left" | "right"` when the centered tooltip would
//     cross the frame edge; component CSS maps those to left/right-aligned
//     positioning (mirror clamp). No attribute = centered (default CSS).
//
// The "chat frame" is the OUTERMOST custom-element host (<emma-chat>) —
// found by walking shadow roots upward — so the same helper serves both the
// quick-chips row (nested three shadow roots deep) and the provider badges
// in <emma-chat-input>. Falls back to the viewport when no host is found
// (defensive; never expected in production).

const EDGE_PAD_PX = 8;
const TOOLTIP_DEFAULT_MAX_PX = 260;
const TOOLTIP_MIN_MAX_PX = 120;

export type TooltipAlign = 'left' | 'right' | '';

/** Pure core — decide the alignment of a centered tooltip of width
 *  `tooltipWidth` anchored to `trigger` inside `frame`. Right edge wins when
 *  both would overflow (the trigger is then hugging the right edge, and a
 *  right-aligned tooltip keeps its visible start inside the frame). */
export function computeTooltipAlign(
  trigger: { left: number; width: number },
  frame: { left: number; right: number },
  tooltipWidth: number,
  pad: number = EDGE_PAD_PX,
): TooltipAlign {
  const center = trigger.left + trigger.width / 2;
  if (center + tooltipWidth / 2 > frame.right - pad) return 'right';
  if (center - tooltipWidth / 2 < frame.left + pad) return 'left';
  return '';
}

/** Cap the tooltip width to the frame width (minus breathing room) so a
 *  narrow embed can never produce a tooltip wider than the frame. */
export function computeTooltipMaxWidth(
  frameWidth: number,
  pad: number = EDGE_PAD_PX,
): number {
  return Math.max(
    TOOLTIP_MIN_MAX_PX,
    Math.min(TOOLTIP_DEFAULT_MAX_PX, frameWidth - pad * 2),
  );
}

/** Walk shadow roots upward and return the OUTERMOST host element's rect —
 *  the <emma-chat> boundary in both standalone and compact embeds. */
function frameRectFor(el: Element): { left: number; right: number; width: number } {
  let host: Element | null = null;
  let node: Element = el;
  for (;;) {
    const root = node.getRootNode();
    if (root instanceof ShadowRoot && root.host) {
      host = root.host;
      node = root.host;
    } else {
      break;
    }
  }
  if (host) {
    const r = host.getBoundingClientRect();
    return { left: r.left, right: r.right, width: r.width };
  }
  return { left: 0, right: window.innerWidth, width: window.innerWidth };
}

/** Apply the clamp to one trigger element. Measures the always-generated
 *  (opacity-0) ::after box when possible; falls back to the CSS default
 *  max-width when the pseudo-element isn't measurable. */
export function applyTooltipClamp(trigger: HTMLElement): void {
  const frame = frameRectFor(trigger);
  const maxW = computeTooltipMaxWidth(frame.width);
  trigger.style.setProperty('--emma-tt-max', `${maxW}px`);
  const tr = trigger.getBoundingClientRect();
  let w = Number.NaN;
  try {
    w = parseFloat(getComputedStyle(trigger, '::after').width);
  } catch {
    /* jsdom / older engines — fall through to the default */
  }
  if (!Number.isFinite(w) || w <= 0) w = TOOLTIP_DEFAULT_MAX_PX;
  w = Math.min(w, maxW);
  const align = computeTooltipAlign(tr, frame, w);
  if (align) trigger.setAttribute('data-tt-align', align);
  else trigger.removeAttribute('data-tt-align');
}

/** Delegated handler for the row container hosting [data-tooltip] triggers.
 *  Bind to BOTH @pointerover and @focusin (pointerenter doesn't bubble). */
export function handleTooltipClampEvent(e: Event): void {
  const target = e.target as Element | null;
  const trigger = target?.closest?.('[data-tooltip]') as HTMLElement | null;
  if (!trigger) return;
  applyTooltipClamp(trigger);
}
